import { vec2 } from "@tlaukkan/tsm";
import React, { MouseEventHandler, useEffect, useState } from "react";
import { Sound } from "../../../shared/sound";
import { getSystem } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { Colors } from "../palettes/colorPalette";
import { UIButtonSmall } from "../styles/ButtonStyle";
import { PlacementSystem } from "../systems/placementSystem";
import { SoundSystem } from "../systems/soundSystem";
import { DefaultSoundEffectVolumes, DistanceVolumeReductionFactor } from "../sound/volumes";

interface MixerPanelProps {
  showMixer: (show: boolean) => void;
}

declare var gameClient: GameClient;

export const MixerPanel = (props: MixerPanelProps) => {
  gameClient.gameTakesKeyEvents = false;
  const soundSystem = gameClient.world.getSystem(SoundSystem) as SoundSystem;

  const [showCopyArea, setShowCopyArea] = React.useState("");

  const hideCopyArea = () => {
    setShowCopyArea("");
  }

  const onMouse: MouseEventHandler<HTMLDivElement> = (e) => {
    //When the mouse is active over a model let the placement system know to 
    //prevent interaction with entities
    const placement = getSystem(gameClient.world, PlacementSystem);
    placement.signalUIInterceptThisFrame();
  };

  const updateSFXVolume = (sound: Sound, value: number) => {
    const effect = soundSystem.soundEngine.getSoundEffect(sound);
    if (effect) effect.volume = value;
  }

  const getSFXVolume = (sound: Sound) => {
    const effect = soundSystem.soundEngine.getSoundEffect(sound);
    if (effect) return effect.volume;
    return -1;
  }

  const exportSounds = () => {
    let output = ``
    for (let i = 0; i < Object.keys(Sound).length; i++) {
      const s = getSFXVolume(i);
      if (s >= 0) {
        output += `[Sound.${Sound[i]}, ${s}],\n`;
      }
    }
    setShowCopyArea(output);
  }

  return (
    <>
      <div id='protection' style={{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%'
      }}
        onMouseDown={onMouse}
        onMouseUp={onMouse}
        onMouseMove={onMouse}
      />
      <div style={{
        width: 'fit-content',
        position: 'absolute',
        right: '0%',
        top: '0%',
        padding: '10px',
        background: Colors.UIBackground,
        border: '5px solid black',
        borderRadius: '15px',
        height: '90%',
        overflow: "hidden",
      }}
      >
        {showCopyArea && <textarea style={{
          position: 'fixed',
          width: "250px",
          height: "400px",
          top: '25%',
          right: '25%',
          translate: 'transform(-50%, -50%)',
        }}
          value={showCopyArea}
          readOnly={true}
          onClick={() => {
            //Select all text in the copy area
            const copyArea = document.querySelector("textarea");
            if (copyArea) {
              copyArea.select();
            }
          }}
        />}
        <div>Sound Effect Mixer</div>
        <div style={{
          width: 'fit-content',
          height: "85%",
          paddingLeft: "50px",
          paddingRight: "50px",
          overflowX: 'hidden',
          overflowY: 'scroll',
        }}
        >
          {Array.from(DefaultSoundEffectVolumes)
            .map((value: [Sound, number], idx) =>
              <div key={idx}>
                <div>{Sound[value[0]]}</div>
                <SoundEffectVolumeSlider sound={value[0]} setVolume={updateSFXVolume} getVolume={getSFXVolume} />
              </div>
            )}
        </div>
        <div style={{ transform: "translate(25%)" }}>
          <UIButtonSmall onClick={() => { props.showMixer(false); gameClient.gameTakesKeyEvents = true; }}>Close</UIButtonSmall>
          <br />
          <UIButtonSmall onClick={() => showCopyArea ? hideCopyArea() : exportSounds()}>Export</UIButtonSmall>
        </div>
      </div>
    </>
  )
}

interface SoundEffectVolumeSliderProps {
  sound: Sound;
  setVolume: (sound: Sound, value: number) => void;
  getVolume: (sound: Sound) => number;
}

// const SoundEffectVolumeSlider = ({ sound, setVolume, getVolume }: SoundEffectVolumeSliderProps) => {
//   const VOLUME_STEPS = 20;
//   const [volume, setVolumeState] = useState(getVolume(sound) * VOLUME_STEPS);

//   const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const newVolume = event.target.value;
//     const volume = parseFloat(newVolume) / VOLUME_STEPS;
//     setVolume(sound, volume);
//     setVolumeState(volume * VOLUME_STEPS);
//   };

//   const playSound = (sound: Sound) => {
//     const soundSystem = gameClient.world.getSystem(SoundSystem) as SoundSystem;
//     if (sound < Sound.UI_SOUND_START) {
//       soundSystem.playSound(sound, new vec2());
//     }
//     else if (sound < Sound.MUSIC_START) {
//       gameClient.playUISound(sound);
//     }
//   }

//   // useEffect(() => {
//   //   const volume = localStorage.getItem(name);
//   //   if (volume) {
//   //     setVolume((parseFloat(volume) * 100).toFixed(0).toString());
//   //   }
//   // }, []);

//   return (
//     <div>
//       <input
//         type="range"
//         min="0"
//         max="200"
//         value={volume}
//         onChange={handleVolumeChange}
//       />
//       <span>{volume}</span>
//       {sound < Sound.MUSIC_START && <button style={{ paddingLeft: "10px" }} onClick={() => playSound(sound)}>Play</button>}
//     </div>
//   );
// };


const SoundEffectVolumeSlider = ({ sound, setVolume, getVolume }: SoundEffectVolumeSliderProps) => {
  const VOLUME_STEPS = 20;
  const [volume, setVolumeState] = useState(getVolume(sound) * VOLUME_STEPS);

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = event.target.value;
    const volume = parseFloat(newVolume) / VOLUME_STEPS;
    updateVolume(volume);
  };

  const handleVolumeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = event.target.value;
    const volume = parseFloat(newVolume) / VOLUME_STEPS;
    if (!isNaN(volume) && volume >= 0) {
      updateVolume(volume);
    }
  };

  const updateVolume = (newVolume: number) => {
    setVolume(sound, newVolume);
    setVolumeState(newVolume * VOLUME_STEPS);
  };

  const playSound = (sound: Sound) => {
    const soundSystem = gameClient.world.getSystem(SoundSystem) as SoundSystem;
    if (sound < Sound.UI_SOUND_START) {
      soundSystem.playSound(sound, new vec2());
    }
    else if (sound < Sound.MUSIC_START) {
      gameClient.playUISound(sound);
    }
  }

  return (
    <div>
      <input
        type="range"
        min="0"
        max="200"
        value={volume}
        onChange={handleVolumeChange}
      />
      <input
        type="number"
        value={volume}
        onChange={handleVolumeInputChange}
        style={{ marginLeft: "10px", width: "60px" }}
      />
      {sound < Sound.MUSIC_START && <button style={{ paddingLeft: "10px" }} onClick={() => playSound(sound)}>Play</button>}
    </div>
  );
};
